import React, { useEffect } from 'react';
import {
  MessageCircle,
  Check,
  Mail,
  Users,
  Building,
  Zap,
  AlertCircle,
  ArrowRight,
  Star,
  DollarSign
} from 'lucide-react';

const ForAgencies = () => {
  useEffect(() => {
    // Google Analytics
    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', 'G-V8ESDMZ5NZ');

    // SEO-OPTIMIZED: Title and meta description
    document.title = 'Customer Support Software for Agencies | TicketDesk';

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Manage support for unlimited clients at one flat rate. No per-seat pricing. Unlimited workspaces for agency client management. Scale plan: $149/month.';

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = 'https://www.ticketdesk.app/for-agencies';

    // Structured data for agencies focus
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "TicketDesk for Agencies",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "offers": {
        "@type": "Offer",
        "price": "149",
        "priceCurrency": "USD"
      },
      "description": "Customer support software for agencies managing multiple clients. Unlimited workspaces, unlimited team members, one flat rate. Perfect for agencies handling client support.",
      "audience": {
        "@type": "Audience",
        "audienceType": "Marketing and support agencies"
      }
    };

    let script = document.querySelector('script[type="application/ld+json"]');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(structuredData);
  }, []);

  const trackEvent = (eventName, eventParams = {}) => {
    if (window.gtag) {
      window.gtag('event', eventName, eventParams);
    }
  };

  const handleStartFreeClick = (location) => {
    trackEvent('start_free_click', {
      button_location: location,
      page: 'for_agencies',
      value: 1
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="/" className="flex items-center min-w-0">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-message-circle">
                  <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719"></path>
                </svg>
              </div>
              <span className="ml-2 sm:ml-3 text-lg sm:text-xl font-semibold text-gray-900">TicketDesk</span>
            </a>
            <div className="flex items-center space-x-2 sm:space-x-4 lg:space-x-8">
              <a href="/#features" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900 hidden md:block">Features</a>
              <a href="/#pricing" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900">Pricing</a>
              <a href="/login" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900 hidden lg:block">Sign In</a>
              <a href="/register" className="bg-purple-600 text-white px-3 sm:px-4 lg:px-5 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium hover:bg-purple-700 whitespace-nowrap">
                Try free
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-12 sm:py-16 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <div className="inline-flex items-center bg-purple-50 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Building className="w-4 h-4 mr-2" />
                Built for Agencies
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
                Customer Support Software for Agencies
              </h1>

              <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                Manage support for unlimited clients at one flat rate. Create separate workspaces for each client with unlimited team members. No per-seat pricing means predictable costs as you scale.
              </p>

              <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 mb-6 sm:mb-8">
                <a
                  href="/register"
                  onClick={() => handleStartFreeClick('hero_section')}
                  className="inline-flex items-center justify-center bg-purple-600 text-white px-5 sm:px-6 py-3 rounded text-sm sm:text-base font-medium hover:bg-purple-700 text-center"
                >
                  Start Free Trial
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
                <a
                  href="https://calendly.com/hello-ticketdesk/demo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center border border-gray-300 text-gray-700 px-5 sm:px-6 py-3 rounded text-sm sm:text-base font-medium hover:border-gray-400 text-center"
                >
                  Book a Demo
                </a>
              </div>

              <p className="text-xs sm:text-sm text-gray-500">No credit card required • Free plan available • Scale plan: $149/month</p>
            </div>

            <div className="mt-8 lg:mt-0">
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-8 shadow-lg">
                <div className="bg-white rounded-xl shadow-md p-6 mb-4 border-l-4 border-blue-500">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <Building className="w-5 h-5 text-blue-600 mr-3" />
                      <div className="flex-1">
                        <div className="font-semibold text-sm">Client A Workspace</div>
                        <div className="text-xs text-gray-500">12 tickets • 3 team members</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-md p-6 mb-4 border-l-4 border-purple-500">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <Building className="w-5 h-5 text-purple-600 mr-3" />
                      <div className="flex-1">
                        <div className="font-semibold text-sm">Client B Workspace</div>
                        <div className="text-xs text-gray-500">8 tickets • 2 team members</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <Building className="w-5 h-5 text-green-600 mr-3" />
                      <div className="flex-1">
                        <div className="font-semibold text-sm">Client C Workspace</div>
                        <div className="text-xs text-gray-500">15 tickets • 4 team members</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Agency Problem Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              The Agency Problem: Per-Seat Pricing Kills Profitability
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Traditional help desk software charges per agent, per client. Your costs explode as you grow.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-red-50 rounded-xl p-8 border-2 border-red-200">
              <div className="flex items-center mb-4">
                <AlertCircle className="w-6 h-6 text-red-600 mr-3" />
                <h3 className="text-xl font-bold text-red-900">With Zendesk/Freshdesk</h3>
              </div>
              <div className="space-y-4">
                <div className="text-gray-700">
                  <div className="font-semibold mb-2">5 Clients × 3 Agents Each:</div>
                  <div className="text-3xl font-bold text-red-600 mb-2">$825/month</div>
                  <div className="text-sm text-gray-600">15 agents × $55/agent (Zendesk)</div>
                </div>
                <div className="pt-4 border-t border-red-200">
                  <div className="font-semibold mb-2">50 Clients × 3 Agents Each:</div>
                  <div className="text-3xl font-bold text-red-600 mb-2">$8,250/month</div>
                  <div className="text-sm text-gray-600">150 agents × $55/agent</div>
                  <div className="text-xs text-red-700 font-medium mt-2">😱 Costs kill your margins!</div>
                </div>
              </div>
            </div>

            <div className="bg-green-50 rounded-xl p-8 border-2 border-green-500">
              <div className="flex items-center mb-4">
                <Check className="w-6 h-6 text-green-600 mr-3" />
                <h3 className="text-xl font-bold text-green-900">With TicketDesk</h3>
              </div>
              <div className="space-y-4">
                <div className="text-gray-700">
                  <div className="font-semibold mb-2">5 Clients × Unlimited Agents:</div>
                  <div className="text-3xl font-bold text-green-600 mb-2">$149/month</div>
                  <div className="text-sm text-gray-600">One flat rate, unlimited workspaces</div>
                </div>
                <div className="pt-4 border-t border-green-200">
                  <div className="font-semibold mb-2">50 Clients × Unlimited Agents:</div>
                  <div className="text-3xl font-bold text-green-600 mb-2">$149/month</div>
                  <div className="text-sm text-gray-600">Still just $149 - no matter how many clients</div>
                  <div className="text-xs text-green-700 font-medium mt-2">💰 Save $8,101/month!</div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-purple-100 rounded-xl p-6 text-center">
            <div className="text-2xl font-bold text-purple-900 mb-2">
              Annual Savings with TicketDesk: $97,212
            </div>
            <div className="text-purple-700">Manage 50 clients for the same price as 3 clients elsewhere</div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Built for Multi-Client Management
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Create separate workspaces for each client with their own tickets, team members, and integrations. Keep client data completely isolated while managing everything from one dashboard.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <Check className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Unlimited client workspaces</div>
                    <div className="text-gray-600">One workspace per client with complete data isolation</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <Check className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Unlimited team members per workspace</div>
                    <div className="text-gray-600">Add client staff without additional per-seat charges</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <Check className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Switch between clients instantly</div>
                    <div className="text-gray-600">Workspace switcher lets you jump between clients in one click</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <Check className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Client-specific integrations</div>
                    <div className="text-gray-600">Each workspace has its own Gmail, Facebook, and review connections</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-12">
              <div className="space-y-4">
                <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-purple-500">
                  <div className="text-sm font-semibold text-gray-900 mb-2">Workspace isolation</div>
                  <div className="text-xs text-gray-600">Each client's data stays completely separate</div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="text-sm font-semibold text-gray-900 mb-2">Quick workspace switching</div>
                  <div className="text-xs text-gray-600">Jump between clients in seconds</div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="text-sm font-semibold text-gray-900 mb-2">Role-based access</div>
                  <div className="text-xs text-gray-600">Control who sees what for each client</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Full Feature List */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Everything Your Agency Needs
            </h2>
            <p className="text-lg text-gray-600">
              All features included at $149/month - no add-ons, no surprises
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <Mail className="w-10 h-10 text-purple-600 mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Multi-Channel Ticketing</h3>
              <p className="text-sm text-gray-600">Gmail, Facebook, WhatsApp, and website widget for each client</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <Star className="w-10 h-10 text-purple-600 mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Review Monitoring</h3>
              <p className="text-sm text-gray-600">Trustpilot, G2, and Capterra alerts for every client workspace</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <Users className="w-10 h-10 text-purple-600 mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Unlimited Team Members</h3>
              <p className="text-sm text-gray-600">Add agency staff and client staff without extra charges</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <Zap className="w-10 h-10 text-purple-600 mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">AI Response Polish</h3>
              <p className="text-sm text-gray-600">Make every response professional with AI assistance</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <Building className="w-10 h-10 text-purple-600 mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Unlimited Workspaces</h3>
              <p className="text-sm text-gray-600">One workspace per client with complete data isolation</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <DollarSign className="w-10 h-10 text-purple-600 mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Predictable Pricing</h3>
              <p className="text-sm text-gray-600">$149/month no matter how many clients you onboard</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Agency Pricing That Actually Makes Sense
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            One flat rate. Unlimited clients. Unlimited team members. Unlimited growth.
          </p>

          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-8 mb-8 max-w-md mx-auto border-2 border-purple-500">
            <div className="inline-flex items-center bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-semibold mb-4">
              <Building className="w-3 h-3 mr-1" />
              SCALE PLAN
            </div>
            <div className="text-5xl font-bold text-purple-600 mb-2">$149</div>
            <div className="text-gray-600 mb-4">per month</div>
            <div className="space-y-2 text-sm text-gray-700 mb-6">
              <div className="flex items-center justify-center">
                <Check className="w-4 h-4 text-green-600 mr-2" />
                <span>Unlimited client workspaces</span>
              </div>
              <div className="flex items-center justify-center">
                <Check className="w-4 h-4 text-green-600 mr-2" />
                <span>Unlimited team members</span>
              </div>
              <div className="flex items-center justify-center">
                <Check className="w-4 h-4 text-green-600 mr-2" />
                <span>5,000 tickets/month</span>
              </div>
              <div className="flex items-center justify-center">
                <Check className="w-4 h-4 text-green-600 mr-2" />
                <span>Unlimited review monitoring</span>
              </div>
            </div>
            <a
              href="/register"
              onClick={() => handleStartFreeClick('pricing_section')}
              className="inline-flex items-center justify-center bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 w-full"
            >
              Start Free Trial
              <ArrowRight className="w-4 h-4 ml-2" />
            </a>
            <p className="text-xs text-gray-500 mt-4">7-day free trial • No credit card required</p>
          </div>

          <div className="bg-blue-50 rounded-xl p-6 max-w-lg mx-auto mb-4">
            <div className="text-sm text-gray-700 mb-2">Quick math:</div>
            <div className="text-lg font-semibold text-gray-900 mb-2">$149 ÷ 50 clients = $2.98 per client</div>
            <div className="text-sm text-gray-600">That's less than a coffee per client per month</div>
          </div>

          <div className="bg-gray-50 rounded-xl p-6 max-w-lg mx-auto mb-8">
            <div className="text-sm text-gray-700 mb-2">Starting small?</div>
            <div className="text-lg font-semibold text-gray-900 mb-2">Free Forever Plan</div>
            <div className="text-sm text-gray-600">10 tickets/month • Test the platform before scaling</div>
          </div>

          <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-600">
            <div className="flex items-center">
              <Check className="w-4 h-4 text-green-500 mr-2" />
              Cancel anytime
            </div>
            <div className="flex items-center">
              <Check className="w-4 h-4 text-green-500 mr-2" />
              No setup fees
            </div>
            <div className="flex items-center">
              <Check className="w-4 h-4 text-green-500 mr-2" />
              30-day money back
            </div>
          </div>

          <div className="mt-8">
            <a href="/#pricing" className="text-purple-600 hover:text-purple-700 font-medium">
              View all plans →
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 lg:py-28 bg-purple-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Scale Your Agency Profitably?
          </h2>
          <p className="text-xl text-white mb-8 opacity-90">
            Join agencies managing unlimited clients at one flat rate
          </p>
          <a
            href="/register"
            onClick={() => handleStartFreeClick('final_cta')}
            className="inline-flex items-center justify-center bg-white text-purple-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100"
          >
            Start Free Trial
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719"></path>
                  </svg>
                </div>
                <span className="ml-3 text-xl font-bold">TicketDesk</span>
              </div>
              <p className="text-gray-400 max-w-md">
                Customer support software for agencies. Unlimited client workspaces at one flat rate. No per-seat pricing.
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><a href="/#features" className="hover:text-white">Features</a></li>
                <li><a href="/#pricing" className="hover:text-white">Pricing</a></li>
                <li><a href="/integrations" className="hover:text-white">Integrations</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Solutions</h3>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><a href="/for-ecommerce" className="hover:text-white">For E-commerce Shops</a></li>
                <li><a href="/for-saas" className="hover:text-white">For SaaS Companies</a></li>
                <li><a href="/for-agencies" className="hover:text-white">For Agencies</a></li>
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2"></div>

            <div>
              <h3 className="font-semibold mb-4">Compare</h3>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><a href="/compare/zendesk" className="hover:text-white">vs Zendesk</a></li>
                <li><a href="/compare/freshdesk" className="hover:text-white">vs Freshdesk</a></li>
                <li><a href="/compare/email" className="hover:text-white">vs Email</a></li>
                <li><a href="/pricing-comparison" className="hover:text-white">Pricing Comparison</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; 2025 TicketDesk. Customer support software for agencies. Manage unlimited clients at one flat rate. No per-seat pricing.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ForAgencies;
